import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from './api.service';
import { ApiResponse } from '../shared/types';

export interface SortOrderRearrangePayload {
  single_wash: { id: string; sort_order: number }[];
  monthly_wash: { id: string; sort_order: number }[];
  add_on: { id: string; sort_order: number }[];
  fleet: { id: string; sort_order: number }[];
  discount: { id: string; sort_order: number }[];
}

/** Preset for PUT `.../locations/:id/sort-order` (sales panel configuration). */
export type LocationSortOrderPreset = 'price_asc' | 'price_desc' | 'name_asc' | 'name_desc';
export type LocationSortOrderType = LocationSortOrderPreset | 'custom_id';
export interface LocationSortOrderListItem {
  id: string;
  name?: string;
  user_id?: string;
  location_id?: string;
  created_at?: string;
  updated_at?: string;
  items?: any[];
}
export interface LocationSortPreferenceData {
  sort_order_type?: LocationSortOrderType | null;
  sort_order_custom_id?: string | null;
  message?: string;
}

@Injectable({ providedIn: 'root' })
export class SortOrderService {
  constructor(private api: ApiService) {}

  createForLocation(locationId: string): Observable<ApiResponse<any>> {
    return this.api.post<any>(`package/sort-order/locations/${locationId}`, null);
  }

  /** Persist default sort for a location (packages / discounts / fleets follow this on the backend). */
  updateLocationSortOrder(
    locationId: string,
    sortOrder: LocationSortOrderPreset
  ): Observable<ApiResponse<any>> {
    return this.api.put<any>(`package/sort-order/locations/${locationId}/sort-order`, {
      sort_order: sortOrder
    });
  }

  /** Persist custom drag-arrange sort preference for a location. */
  updateLocationCustomSortOrder(
    locationId: string,
    customId: string
  ): Observable<ApiResponse<any>> {
    return this.api.put<any>(`package/sort-order/locations/${locationId}/sort-order`, {
      sort_order: 'custom_id',
      custom_id: customId
    });
  }

  /** Clear saved sort preference for a location (Clear Filters in Sort modal). */
  clearLocationSortOrder(locationId: string): Observable<ApiResponse<any>> {
    return this.api.put<any>(`package/sort-order/locations/${locationId}/sort-order`, {
      sort_order: null,
      custom_id: null
    });
  }

  /** Fetch selected sort preference for a location to prefill Configure Sales Panel. */
  getLocationSortPreference(locationId: string): Observable<ApiResponse<LocationSortPreferenceData>> {
    return this.api.get<LocationSortPreferenceData>(`package/sort-order/locations/${locationId}/sort-preference`);
  }

  /** Fetch all custom sort orders created for a location. */
  getLocationSortOrders(locationId: string): Observable<ApiResponse<LocationSortOrderListItem[]>> {
    return this.api.get<LocationSortOrderListItem[]>(`package/sort-order/locations/${locationId}`);
  }

  /** Rename a saved custom sort list. */
  renameSortOrder(sortOrderId: string, name: string): Observable<ApiResponse<any>> {
    return this.api.put<any>(`package/sort-order/${sortOrderId}`, { name });
  }

  /** Delete a saved custom sort list. */
  deleteSortOrder(sortOrderId: string): Observable<ApiResponse<any>> {
    return this.api.delete<any>(`package/sort-order/${sortOrderId}`);
  }

  rearrange(sortOrderId: string, body: SortOrderRearrangePayload): Observable<ApiResponse<any>> {
    return this.api.put<any>(`package/sort-order/${sortOrderId}/rearrange`, body);
  }
}

/** Extract `sort_order` row id from POST 201 body (handles common shapes). */
export function parseSortOrderIdFromCreateResponse(data: any): string | null {
  if (data == null) {
    return null;
  }
  if (typeof data === 'string' && data.trim()) {
    return data.trim();
  }
  if (typeof data === 'object' && !Array.isArray(data)) {
    const o = data as Record<string, any>;
    const candidates = [o['id'], o['sort_order_id'], (o['data'] as Record<string, any> | undefined)?.['id']];
    for (const c of candidates) {
      if (c != null && String(c).trim() !== '') {
        return String(c).trim();
      }
    }
  }
  return null;
}
